import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {Object} StudentLimitData
 * @property {number|string} id - The student ID
 * @property {number|string} limit - The new quiz limit for the student
 */

/**
 * @typedef {Object} SetQuizLimitStudentData
 * @property {number|string} classroom_id - The classroom ID
 * @property {StudentLimitData[]} students - Array of students with their new limits
 */

/**
 * @description Custom hook to set quiz limits for specific students in a classroom
 */
export const useQuizLimitStudent = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const { axiosInstance } = useAxios();

    /**
     * Set quiz limits for specific students
     * @async
     * @param {SetQuizLimitStudentData} data - The quiz limit data for students
     * @param {(success:boolean) => void} [onSuccess] - Callback on success
     * @returns {Promise<any|null>} The response data or null on failure
     */
    const setQuizLimitStudent = async (data, onSuccess) => {
        try {
            setLoading(true);
            setError("");
            setSuccess(false);

            const url = `${TENANT_DOMAIN}/quiz/limit/student/${data.classroom_id}`;
            const response = await axiosInstance.post(url, {
                students: data.students,
            });

            const responseData = response?.data?.data || null;

            setSuccess(true);
            if (onSuccess) onSuccess(true);
            return responseData;
        } catch (err) {
            setSuccess(false);
            if (onSuccess) onSuccess(false);

            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                const apiErrors = err?.response?.data?.errors;
                const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

                setError(
                    firstError ||
                    err?.response?.data?.message ||
                    ERROR_MESSAGES.UNKNOWN,
                );
            }
            return null;
        } finally {
            setLoading(false);
        }
    };


    return {
        loading,
        error,
        success,
        setQuizLimitStudent,
        setError,
    };
};
